import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import type { Stripe } from '@stripe/stripe-js'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { fetchLuxPlusPreferredPaymentCard, fetchLuxPlusSubscriptionRenewalPaymentCards } from 'actions/LuxPlusActions'
import { getSavedStripeCards } from 'api/payment'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getStripeInstance, { type StripeInstanceResult } from 'lib/payment/getStripeInstance'
import { LUX_PLUS_MANAGE_ACCOUNT_HASH, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import LuxPlusManageAccountModalSavingsBanner from 'luxPlus/components/LuxPlusManageAccount/LuxPlusManageAccountModalSavingsBanner'
import updateLuxPlusPrimaryCard from 'luxPlus/components/LuxPlusManageAccount/updateLuxPlusPrimaryCard'
import useLuxPlusSubscriptionProduct from 'luxPlus/hooks/useLuxPlusSubscriptionProduct'
import getLuxPlusInvalidCardReason from 'luxPlus/lib/getLuxPlusInvalidCardReason'
import getLuxPlusInvalidCardReplacementCards from 'luxPlus/lib/getLuxPlusInvalidCardReplacementCards'
import { getLuxPlusSubscriptionRenewalDate } from 'selectors/luxPlusSelectors'
import LuxPlusInvalidCardModalBenefits from './LuxPlusInvalidCardModalBenefits'
import LuxPlusInvalidCardModalCardPicker from './LuxPlusInvalidCardModalCardPicker'
import LuxPlusInvalidCardModalCardRow from './LuxPlusInvalidCardModalCardRow'

const LUX_PLUS_MANAGE_ACCOUNT_PATH = `/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}${LUX_PLUS_MANAGE_ACCOUNT_HASH}`

export interface LuxPlusInvalidCard extends App.LuxPlusSubscriptionRenewalPaymentCard {
  card: App.StripePaymentCardV2
}

interface Props {
  /** The member's LP+ renewal cards, all flagged invalid, in priority order. */
  invalidCards: Array<LuxPlusInvalidCard>
}

/**
 * Shown when every LuxPlus+ renewal card has been flagged as likely to fail.
 * Content follows the LuxPlus+ renewal reminder email (Figma UX-4862), without the renewal bonus: the bonus
 * ladder is only in the email behind the tiered renewal reward experiment.
 * The member can promote one saved card (no CVV); svc-payment then removes the invalid cards.
 */
function LuxPlusInvalidCardModal({ invalidCards }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const modalContext = useModalElementContext()
  const subscription = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item)
  const renewalDate = useAppSelector(getLuxPlusSubscriptionRenewalDate)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const currencyCode = useAppSelector(state => state.geo.currentCurrency)
  const [savedCards, setSavedCards] = useState<Array<App.StripePaymentCardV2>>()
  const [stripe, setStripe] = useState<Stripe>()
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)
  const [promotedCard, setPromotedCard] = useState<App.StripePaymentCardV2>()
  // The savings banner compares against the membership price from the subscription offers
  useLuxPlusSubscriptionProduct()

  const benefits = subscription?.benefits ?? []
  const invalidCardLast4List = intl.formatList(invalidCards.map(item => item.card.last4), { type: 'conjunction' })
  const cardsWithReason = useMemo(
    () => invalidCards.map(item => ({ item, reason: getLuxPlusInvalidCardReason(item) })),
    [invalidCards],
  )
  const expiredCardLast4s = cardsWithReason.filter(({ reason }) => reason === 'expired').map(({ item }) => item.card.last4)
  const notValidCardLast4s = cardsWithReason.filter(({ reason }) => reason === 'invalid').map(({ item }) => item.card.last4)

  let intro: string
  if (expiredCardLast4s.length > 0 && notValidCardLast4s.length > 0) {
    intro = intl.formatMessage({
      id: 'hgQVhe3D1SrKMMhPVcWI',
      defaultMessage: 'It appears that the {expiredCount, plural, one {card} other {cards}} ending {expiredLast4List} {expiredCount, plural, one {is} other {are}} expired, and the {notValidCount, plural, one {card} other {cards}} ending {notValidLast4List} {notValidCount, plural, one {is} other {are}} not valid, so please update your card details so you don’t miss out on renewal benefits.',
      description: 'Intro of the LuxPlus+ invalid card modal when some renewal cards have expired and others were flagged as not valid, naming the last four digits of each',
    }, {
      expiredCount: expiredCardLast4s.length,
      expiredLast4List: intl.formatList(expiredCardLast4s, { type: 'conjunction' }),
      notValidCount: notValidCardLast4s.length,
      notValidLast4List: intl.formatList(notValidCardLast4s, { type: 'conjunction' }),
    })
  } else if (expiredCardLast4s.length > 0) {
    intro = intl.formatMessage({
      id: 'HFjt3wmYkVHkd5j9xSJ_',
      defaultMessage: 'It appears that the {cardCount, plural, one {card} other {cards}} ending {cardLast4List} {cardCount, plural, one {is} other {are}} expired, so please update your card details so you don’t miss out on renewal benefits.',
      description: 'Intro of the LuxPlus+ invalid card modal when every renewal card has expired, naming the last four digits of each',
    }, {
      cardCount: expiredCardLast4s.length,
      cardLast4List: intl.formatList(expiredCardLast4s, { type: 'conjunction' }),
    })
  } else {
    intro = intl.formatMessage({
      id: 'NTuMENZqlmmmcF4BibSz',
      defaultMessage: 'It appears that the {cardCount, plural, one {card} other {cards}} ending {cardLast4List} {cardCount, plural, one {is} other {are}} not valid, so please update your card details so you don’t miss out on renewal benefits.',
      description: 'Intro of the LuxPlus+ invalid card modal when every renewal card was flagged as not valid for a reason other than expiry, naming the last four digits of each',
    }, {
      cardCount: notValidCardLast4s.length,
      cardLast4List: intl.formatList(notValidCardLast4s, { type: 'conjunction' }),
    })
  }

  useEffect(() => {
    let isCancelled = false
    getSavedStripeCards(currencyCode)
      .then(cards => {
        if (!isCancelled) setSavedCards(cards)
      })
      .catch(() => {
        if (!isCancelled) setSavedCards([])
      })
    getStripeInstance(currencyCode)
      .then((result: StripeInstanceResult) => {
        if (!isCancelled) setStripe(result.stripe ?? undefined)
      })
      .catch(() => {
        if (!isCancelled) setStripe(undefined)
      })

    return () => {
      isCancelled = true
    }
  }, [currencyCode])

  const replacementCards = useMemo(
    () => getLuxPlusInvalidCardReplacementCards(savedCards ?? [], invalidCards, renewalDate),
    [invalidCards, renewalDate, savedCards],
  )
  const selectedCard = replacementCards.find(card => card.paymentMethodId === selectedPaymentMethodId)
  const hasLoadedSavedCards = !!savedCards
  const hasReplacementCards = replacementCards.length > 0

  const onClose = useCallback(() => modalContext?.resolve(), [modalContext])

  const onSelectCard = useCallback((paymentMethodId: string) => {
    setSelectedPaymentMethodId(paymentMethodId)
    setHasFailed(false)
  }, [])

  const onUseSelectedCard = useCallback(async() => {
    if (!selectedCard || !stripe || !subscription) return

    setIsSubmitting(true)
    setHasFailed(false)
    try {
      await updateLuxPlusPrimaryCard({
        currencyCode,
        paymentMethodId: selectedCard.paymentMethodId,
        purposeId: subscription.id,
        regionCode,
        removeInvalidCards: true,
        stripe,
      })
      // The webhook replaces the primary and removes the invalid cards a moment later
      dispatch(fetchLuxPlusSubscriptionRenewalPaymentCards({ refetch: true }))
      dispatch(fetchLuxPlusPreferredPaymentCard())
      setPromotedCard(selectedCard)
    } catch {
      setHasFailed(true)
    } finally {
      setIsSubmitting(false)
    }
  }, [currencyCode, dispatch, regionCode, selectedCard, stripe, subscription])

  if (promotedCard) {
    return <Modal
      size="S"
      dismissible
      title={intl.formatMessage({
        id: 'LS5zxytmSN2Su5KpoZ_T',
        defaultMessage: 'Your renewal card is updated',
        description: 'Title of the LuxPlus+ invalid card modal after the member successfully sets a new renewal card',
      })}
      primaryActionText={intl.formatMessage({
        id: 's8U5bldzi0V6eEw2KFw3',
        defaultMessage: 'Done',
        description: 'Button closing the LuxPlus+ invalid card modal after the member successfully sets a new renewal card',
      })}
      onPrimaryActionClick={onClose}
    >
      <BodyText variant="large">
        {intl.formatMessage({
          id: 'NxUbjs6ni__1YXuMpQwB',
          defaultMessage: 'Card ending {newCardLast4} is now your renewal card. We’ve removed {removedCardCount, plural, one {card} other {cards}} ending {removedCardLast4List}.',
          description: 'Confirmation in the LuxPlus+ invalid card modal naming the new renewal card and the invalid cards that were removed',
        }, {
          newCardLast4: promotedCard.last4,
          removedCardCount: invalidCards.length,
          removedCardLast4List: invalidCardLast4List,
        })}
      </BodyText>
    </Modal>
  }

  let primaryActionText: string
  if (!hasReplacementCards) {
    primaryActionText = intl.formatMessage({
      id: 'euTV28f3JxuTMjy-GeIG',
      defaultMessage: 'Update payment method',
      description: 'Button in the LuxPlus+ invalid card modal taking a member with no usable saved cards to their LuxPlus+ membership page',
    })
  } else if (isSubmitting) {
    // Not the Modal `loading` prop: that closes the modal when loading ends, including on failure
    primaryActionText = intl.formatMessage({
      id: 'nozhlkrH0O9VCj-5M29z',
      defaultMessage: 'Updating your card…',
      description: 'Button label in the LuxPlus+ invalid card modal while the selected saved card is being set as the renewal card',
    })
  } else {
    primaryActionText = intl.formatMessage({
      id: 'PyIWl9uF8MviC7nAi7oq',
      defaultMessage: 'Use this card for renewal',
      description: 'Button in the LuxPlus+ invalid card modal setting the selected saved card as the new renewal card',
    })
  }

  const addNewCardText = intl.formatMessage({
    id: 'lECB-78nFpHFJyMvHsMC',
    defaultMessage: 'Add a new card',
    description: 'Link in the LuxPlus+ invalid card modal taking the member to their LuxPlus+ membership page to add a new card',
  })

  return <Modal
    size="S"
    dismissible
    title={intl.formatMessage({
      id: 'T_wUILWEDb_qTghx2wXv',
      defaultMessage: 'Check your LuxPlus+ renewal card',
      description: 'Title of the modal shown to LuxPlus+ members when every card saved for their renewal is likely to fail, asking them to check it',
    })}
    isPrimaryActionHidden={!hasLoadedSavedCards}
    isSecondaryActionHidden={!hasLoadedSavedCards || !hasReplacementCards}
    primaryActionText={primaryActionText}
    primaryActionTo={hasReplacementCards ? undefined : LUX_PLUS_MANAGE_ACCOUNT_PATH}
    onPrimaryActionClick={hasReplacementCards ? onUseSelectedCard : onClose}
    isPrimaryActionDisabled={hasReplacementCards && (!selectedCard || !stripe || isSubmitting)}
    secondaryActionText={addNewCardText}
    secondaryActionTo={LUX_PLUS_MANAGE_ACCOUNT_PATH}
    onSecondaryActionClick={onClose}
  >
    <Group direction="vertical" gap={20}>
      <BodyText variant="large">{intro}</BodyText>
      <Group direction="vertical" gap={8}>
        {cardsWithReason.map(({ item, reason }) => <LuxPlusInvalidCardModalCardRow
          key={item.id}
          card={item.card}
          isPreferred={item.priority === 0}
          reason={reason}
        />)}
      </Group>
      {hasReplacementCards && <LuxPlusInvalidCardModalCardPicker
        cards={replacementCards}
        disabled={isSubmitting}
        selectedPaymentMethodId={selectedPaymentMethodId}
        onSelect={onSelectCard}
      />}
      {hasFailed && <MessageBanner kind="critical">
        {intl.formatMessage({
          id: 'UkoeLZ2O6C2LiDU908Hu',
          defaultMessage: 'We couldn’t verify this card. Try another card or add a new one.',
          description: 'Error in the LuxPlus+ invalid card modal when the selected saved card could not be set as the renewal card',
        })}
      </MessageBanner>}
      <LuxPlusManageAccountModalSavingsBanner />
      <LuxPlusInvalidCardModalBenefits benefits={benefits} />
    </Group>
  </Modal>
}

export default LuxPlusInvalidCardModal
